import { type FormEvent, useState } from 'react';

import { failureMessage, refusedField } from './failure.ts';

export interface EmailRequestFormProps {
  /** Prefixes every control's `data-test-id`, so two of these on different screens stay apart. */
  testIdPrefix: string;
  /** What the field starts with, such as the address already typed into a sign-in form. */
  initialEmail?: string;
  submitLabel: string;
  pendingLabel: string;
  send: (email: string) => Promise<void>;
  /** Called with the address once the server has taken the request. */
  onSent: (email: string) => void;
}

/**
 * One address and a button: the whole of asking for a verification link again or for a reset
 * link. What happens next is the same screen whichever address was typed, so this form's only
 * outcomes are "taken", a throttle and no connection.
 */
export const EmailRequestForm = ({
  testIdPrefix,
  initialEmail = '',
  submitLabel,
  pendingLabel,
  send,
  onSent,
}: EmailRequestFormProps) => {
  const [email, setEmail] = useState(initialEmail);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The server's refusal of the address itself, shown beside the field rather than below it. */
  const [emailError, setEmailError] = useState<string | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError(null);
    setEmailError(null);

    void send(email).then(
      () => onSent(email),
      (failure: unknown) => {
        setPending(false);
        if (refusedField(failure) === 'email') {
          setEmailError(failureMessage(failure));
        } else {
          setError(failureMessage(failure));
        }
      },
    );
  };

  return (
    <form onSubmit={submit}>
      <label htmlFor={`${testIdPrefix}-email`}>E-mail</label>
      <input
        id={`${testIdPrefix}-email`}
        data-test-id={`${testIdPrefix}-email`}
        type="email"
        autoComplete="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        aria-invalid={emailError !== null}
        aria-describedby={emailError === null ? undefined : `${testIdPrefix}-email-error`}
        required
      />
      {emailError === null ? null : (
        <p
          className="login-error"
          id={`${testIdPrefix}-email-error`}
          data-test-id={`${testIdPrefix}-email-error`}
          role="alert"
        >
          {emailError}
        </p>
      )}

      {error === null ? null : (
        <p className="login-error" data-test-id={`${testIdPrefix}-error`} role="alert">
          {error}
        </p>
      )}

      <button type="submit" data-test-id={`${testIdPrefix}-submit`} disabled={pending}>
        {pending ? pendingLabel : submitLabel}
      </button>
    </form>
  );
};
